// components/market/ColorPickerField.tsx
//
// Champ de sélection d'une couleur, utilisé dans l'écran Réglages
// (app/(tabs)/market/reglages.tsx) pour chaque clé de ui_config.colors.
// Affiche un bouton compact (pastille + valeur hex) qui ouvre un vrai
// color picker interactif (glisser dans le panneau + slider de teinte)
// via reanimated-color-picker, choisi pour sa fiabilité multi-plateforme
// (iOS/Android/Expo/Web) plutôt qu'un picker construit à la main.
//
// Valide le format ^#[0-9A-Fa-f]{6}$ avant d'appeler onChange, en
// miroir de la contrainte CHECK SQL (market_validate_shop_ui_config) —
// colorKit.HEX() normalise toujours vers ce format donc la validation
// est surtout un garde-fou de sécurité, pas un cas attendu en pratique.
//
// PATCH i18n : les boutons Annuler/Valider de la modale passent par
// react-i18next (namespace market.components.colorPicker). Le `label`
// reçu en prop (ex: "Couleur principale") est déjà traduit par l'écran
// parent (gerer/[shopId].tsx) — ce composant ne le traduit pas lui-même.

import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, Modal, StyleSheet, Platform } from 'react-native';
import ColorPicker, { Panel1, Preview, HueSlider } from 'reanimated-color-picker';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';

const NATIVE = Platform.OS !== 'web';
const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

type ColorPickerFieldProps = {
  label: string;
  value: string; // format hex, ex: '#6610B1'
  onChange: (nextHex: string) => void;
};

export default function ColorPickerField({ label, value, onChange }: ColorPickerFieldProps) {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  // Couleur de brouillon pendant que la modal est ouverte — onChange
  // n'est appelé qu'à la confirmation (bouton "Valider"), pour éviter
  // de déclencher une sauvegarde/re-render à chaque micro-mouvement du
  // doigt sur le panneau.
  const [draftHex, setDraftHex] = useState(value);

  const openPicker = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setDraftHex(value);
    setModalVisible(true);
  }, [value]);

  const closePicker = useCallback(() => {
    setModalVisible(false);
  }, []);

  const handleChangeJS = useCallback(({ hex }: { hex: string }) => {
    setDraftHex(hex);
  }, []);

  const handleConfirm = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    if (HEX_PATTERN.test(draftHex)) {
      onChange(draftHex);
    }
    setModalVisible(false);
  }, [draftHex, onChange]);

  return (
    <>
      <TouchableOpacity style={styles.row} activeOpacity={0.7} onPress={openPicker}>
        <View style={styles.rowIconWrap}>
          <View style={[styles.swatch, { backgroundColor: value }]} />
        </View>
        <Text style={styles.rowLabel}>{label}</Text>
        <Text style={styles.rowSub}>{value.toUpperCase()}</Text>
        <Ionicons name="chevron-forward" size={16} color="#C4B5FD" />
      </TouchableOpacity>

      <Modal visible={modalVisible} transparent animationType="fade" onRequestClose={closePicker}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{label}</Text>
              <TouchableOpacity onPress={closePicker} activeOpacity={0.7}>
                <Ionicons name="close" size={22} color="#666" />
              </TouchableOpacity>
            </View>

            <ColorPicker
              value={draftHex}
              onChangeJS={handleChangeJS}
              style={styles.picker}
            >
              <Preview style={styles.preview} />
              <Panel1 style={styles.panel} />
              <HueSlider style={styles.hueSlider} />
            </ColorPicker>

            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={closePicker} activeOpacity={0.7}>
                <Text style={styles.cancelBtnText}>{t('market.components.colorPicker.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.confirmBtn} onPress={handleConfirm} activeOpacity={0.8}>
                <Text style={styles.confirmBtnText}>{t('market.components.colorPicker.confirm')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 13,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  rowIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  swatch: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
  },
  rowLabel: {
    flex: 1,
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  rowSub: {
    fontSize: 13,
    color: '#999',
    marginRight: 8,
    fontVariant: ['tabular-nums'],
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalCard: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#222',
  },
  picker: {
    width: '100%',
  },
  preview: {
    borderRadius: 12,
    marginBottom: 16,
  },
  panel: {
    borderRadius: 12,
    marginBottom: 16,
  },
  hueSlider: {
    borderRadius: 20,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 20,
  },
  cancelBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#374151',
  },
  confirmBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: '#6610B1',
    alignItems: 'center',
  },
  confirmBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#fff',
  },
});
